import {
  Component,
  input,
  output,
  signal,
  computed,
  viewChild,
  ElementRef,
  afterNextRender,
  ChangeDetectionStrategy,
  HostListener,
} from '@angular/core';
import { Claim } from '../../../claim-annotator/domain/types';

@Component({
  selector: 'app-claim-picker-popover',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="bg-white border border-slate-300 rounded shadow-lg p-2 text-sm w-64 text-slate-900">
      <div class="text-slate-600 mb-2">Assign box to claim:</div>
      <input
        #search
        type="text"
        class="input-field mb-2"
        placeholder="Search questions..."
        data-testid="claim-search"
        [value]="query()"
        (input)="onQuery($any($event.target).value)"
        (keydown)="onKeydown($event)"
      />
      <div class="max-h-64 overflow-y-auto" data-testid="claim-list">
        @for (c of filtered(); track c.id; let i = $index) {
          <button
            [attr.data-claim-id]="c.id"
            class="block w-full text-left px-2 py-1 rounded hover:bg-slate-100"
            [class.bg-slate-100]="i === activeIndex()"
            (click)="picked.emit(c.id)"
          >
            {{ c.text }}
          </button>
        }
        @if (filtered().length === 0) {
          <div class="px-2 py-1 text-slate-400" data-testid="claim-empty">
            No matching questions
          </div>
        }
      </div>
      <button
        data-cancel
        class="mt-2 w-full px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100"
        (click)="cancelled.emit()"
      >
        Cancel (Esc)
      </button>
    </div>
  `,
})
export class ClaimPickerPopoverComponent {
  readonly claims = input.required<Claim[]>();
  readonly picked = output<string>();
  readonly cancelled = output<void>();

  private readonly searchRef = viewChild<ElementRef<HTMLInputElement>>('search');
  readonly query = signal('');
  readonly activeIndex = signal(0);
  readonly filtered = computed(() => {
    const q = this.query().toLowerCase();
    return this.claims().filter((c) => c.text.toLowerCase().includes(q));
  });

  constructor() {
    afterNextRender(() => this.searchRef()?.nativeElement.focus());
  }

  onQuery(v: string): void {
    this.query.set(v);
    this.activeIndex.set(0);
  }

  onKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      ev.stopPropagation();
      this.activeIndex.update((i) => Math.min(i + 1, this.filtered().length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      ev.stopPropagation();
      this.activeIndex.update((i) => Math.max(i - 1, 0));
    } else if (ev.key === 'Enter') {
      if (ev.isComposing || ev.keyCode === 229) return;
      ev.preventDefault();
      ev.stopPropagation();
      const c = this.filtered()[this.activeIndex()];
      if (c) this.picked.emit(c.id);
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(ev: Event): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.cancelled.emit();
  }
}
